import axios from 'axios';
import Decimal from 'decimal.js';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MonitoringWallet, WalletFormData } from '../types/types';
import { formatAddress, formatTimestamp } from '../utils/dateUtils';
import './Wallets.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Wallets = () => {
  const [wallets, setWallets] = useState<MonitoringWallet[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingWallet, setEditingWallet] = useState<string | null>(null);
  const [searchAddress, setSearchAddress] = useState('');
  const [formData, setFormData] = useState<WalletFormData>({
    walletAddress: '',
    name: '',
    amount: '',
  });
  const navigate = useNavigate();

  // Refresh token을 사용해서 새로운 access token을 받아오는 함수
  const refreshAccessToken = async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return false;
      }

      const response = await axios.patch(`${API_BASE_URL}/auth/refresh`, {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      });

      const { accessToken } = response.data;
      localStorage.setItem('accessToken', accessToken);
      return true;
    } catch (error) {
      console.error('토큰 갱신 실패:', error);
      return false;
    }
  };

  // 강제 로그아웃 함수
  const forceLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/');
  };

  // 401 에러 처리 함수
  const handle401Error = async (): Promise<boolean> => {
    const refreshSuccess = await refreshAccessToken();
    if (!refreshSuccess) {
      forceLogout();
      return false;
    }
    return true;
  };

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/');
    } else {
      fetchWallets();
    }
  }, [navigate]);

  const fetchWallets = async (address = '') => {
    setLoading(true);
    try {
      const url = address 
        ? `${API_BASE_URL}/monitorings/wallets?walletAddress=${address}`
        : `${API_BASE_URL}/monitorings/wallets`;
        
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      // 응답 데이터가 배열인지 확인, 아니면 빈 배열로 처리
      const responseData = response.data.data || [];
      const walletArray = Array.isArray(responseData) ? responseData : [];
      console.log(walletArray);
      // Convert string amount to Decimal and date strings to Date objects
      const walletData = walletArray.map((wallet: any) => ({
        ...wallet,
        amount: new Decimal(wallet.amount),
        createdAt: new Date(wallet.createdAt),
        modifiedAt: wallet?.modifiedAt ? new Date(wallet.modifiedAt) : undefined
      }));
      
      setWallets(walletData);
    } catch (error: any) {
      if (error.response?.status === 401) {
        const refreshSuccess = await handle401Error();
        if (refreshSuccess) {
          // 토큰 갱신 성공 시 재시도
          fetchWallets(address);
          return;
        }
      }
      console.error('지갑 목록 조회 실패:', error);
      alert('지갑 목록을 불러오는데 실패했습니다.');
      setWallets([]); // 오류 발생 시 빈 배열로 설정
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API_BASE_URL}/monitorings/wallets`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      // 성공 메시지 표시
      alert('지갑 정보가 성공적으로 저장되었습니다.');
      
      // Reset form and fetch updated list
      setFormData({
        walletAddress: '',
        name: '',
        amount: '',
      });
      setShowForm(false);
      setEditingWallet(null);
      
      // 목록 새로고침 추가
      fetchWallets();
    } catch (error: any) {
      if (error.response?.status === 401) {
        const refreshSuccess = await handle401Error();
        if (refreshSuccess) {
          // 토큰 갱신 성공 시 재시도
          handleSubmit(e);
          return;
        }
      }
      console.error('지갑 저장 실패:', error);
      alert('지갑 정보 저장에 실패했습니다.');
    }
  };

  const handleEdit = (wallet: MonitoringWallet) => {
    setFormData({
      walletAddress: wallet.walletAddress,
      name: wallet.name,
      amount: wallet.amount.toString(),
    });
    setEditingWallet(wallet.walletAddress);
    setShowForm(true);
  };

  const handleDelete = async (walletAddress: string) => {
    // 삭제 확인 창 표시
    if (window.confirm('정말로 이 지갑을 삭제하시겠습니까?')) {
      try {
        await axios.delete(`${API_BASE_URL}/monitorings/wallets/${walletAddress}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        
        // 삭제 후 목록 새로고침
        fetchWallets();
        alert('지갑이 성공적으로 삭제되었습니다.');
      } catch (error: any) {
        if (error.response?.status === 401) {
          const refreshSuccess = await handle401Error();
          if (refreshSuccess) {
            // 토큰 갱신 성공 시 재시도
            handleDelete(walletAddress);
            return;
          }
        }
        console.error('지갑 삭제 실패:', error);
        alert('지갑 삭제에 실패했습니다.');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchWallets(searchAddress);
  };

  return (
    <div className="wallets-container">
      <div className="header">
        <h1>TRIX Wallet Monitoring</h1>
        <button 
          className="logout-button"
          onClick={handleLogout}
        >
          로그아웃
        </button>
      </div>

      <div className="actions">
        <button 
          className="add-button"
          onClick={() => {
            setFormData({
              walletAddress: '',
              name: '',
              amount: '',
            });
            setEditingWallet(null);
            setShowForm(!showForm);
          }}
        >
          {showForm ? '취소' : '지갑 추가'}
        </button>
      </div>

      <div className="search-form">
        <form onSubmit={handleSearch}>
          <div className="search-container">
            <input
              type="text"
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              placeholder="지갑 주소로 검색..."
              className="search-input"
            />
            <button type="submit" className="search-button">검색</button>
            {searchAddress && (
              <button 
                type="button" 
                className="reset-button"
                onClick={() => {
                  setSearchAddress('');
                  fetchWallets('');
                }}
              >
                초기화
              </button>
            )}
          </div>
        </form>
      </div>

      {showForm && (
        <div className="wallet-form">
          <h2>{editingWallet ? '지갑 수정' : '지갑 추가'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>지갑 주소</label>
              <input
                type="text"
                value={formData.walletAddress}
                onChange={(e) => setFormData({...formData, walletAddress: e.target.value})}
                placeholder="0x..."
                required
                disabled={!!editingWallet}
              />
            </div>
            <div className="form-group">
              <label>이름</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="지갑 소유자 이름"
                required
              />
            </div>
            <div className="form-group">
              <label>모니터링 최소 수량 (ETH단위)</label>
              <input
                type="text"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.0"
                required
              />
            </div>
            <button type="submit">저장</button>
          </form>
        </div>
      )}

      <div className="wallet-list">
        <h2>지갑 목록</h2>
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>지갑 주소</th>
                <th>이름</th>
                <th>모니터링 최소 수량(ETH단위)</th>
                <th>생성일</th>
                <th>수정일</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {wallets.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-data">등록된 지갑이 없습니다.</td>
                </tr>
              ) : (
                wallets.map((wallet) => (
                  <tr key={wallet.walletAddress}>
                    <td>
                      <span title={wallet.walletAddress}>{formatAddress(wallet.walletAddress)}</span>
                    </td>
                    <td>{wallet.name}</td>
                    <td>{wallet.amount.toString()}</td>
                    <td>{formatTimestamp(wallet.createdAt)}</td>
                    <td>{formatTimestamp(wallet?.modifiedAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-button"
                          onClick={() => handleEdit(wallet)}
                        >
                          수정
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(wallet.walletAddress)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Wallets;